import moment from 'moment'
import { throttle } from 'lodash'
import { pick, pipe, values } from 'ramda'
import { memo, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Box, Text, ResponsiveRender, MenuItem } from '@machobear/design-system'
import {
  MenuItemLink,
  OverflowMenu,
  ButtonLink,
  TooltipRenderer,
  Stack
} from '@machobear/design-system'
import { Avatar } from '@/components/avatar'
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  StyledTableCell,
  SingleTableRow,
  TableHeaderText,
  MobileUnitText,
  MobileUnitDetails,
  MobileRowWrapper
} from '@/components/listings-table'
import useMediaQuery from '@/hooks/use-media-query'
import { select } from '@/store'
import { colors, breakpoints, structure } from '@/styles'
import { isTruthy } from '@/utils'
import formatName from '@/utils/format-name'
import { formatSalaryRange } from '@/utils/format-salary'
import { getImageUrl } from '@/utils/image'
import { toUnit } from '@/utils/styles'
import config from '@/config'
import { showCopyApplicationModal } from '@/components/copy-application-modal'
import useProfile from '@/hooks/use-profile'
import { ApplicationsEmptyListComponent } from '@/components/table-list'
import useEventListener from '@/hooks/use-event-listener'
import { LoadingCircle } from '@/components/loading-circle'
import { pushToDatalayer } from '@/utils/push-datalayer'
import { ApplicationHighlight, ContractHighlight } from './tours'
import ListingApplicationsHeader from './listing-applications-header'
import HeartUnselected from './images/heart.svg'
import HeartSelected from './images/heart-active.svg'
import TrustScoreComponent from './trustscore-component'
import EmptyApplications from './empty-applications'
import SkeletonState from './applications-skeleton'

const Wrapper = styled.div`
  min-height: calc(100vh - ${toUnit(structure.header.height)});
  background-color: ${colors.white};
  padding: 0 0 220px;
  flex: 1;
  display: flex;
  flex-direction: column;

  background-repeat: no-repeat;
  background-position: 100% 100%;
  background-size: 542px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-height: calc(100vh - ${toUnit(structure.header.heightMobile)});
    padding-bottom: 125px;

    background-repeat: no-repeat;
    background-position: 100% 100%;
    background-size: 375px;
  }
`

const TableOutterWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`

const TableWrapper = styled.div`
  flex: 1;
  max-width: 1130px;
  margin: 0 50px;

  @media only screen and (max-width: ${breakpoints.desktop}) {
    margin: 0 30px;
  }

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin: 0 30px;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0;
  }
`

const ApplicationTable = styled(Table)`
  width: 100%;
  border: 1px solid ${colors.mediumLightGrey};
  box-shadow: 0 2px 8px 0 ${colors.mediumLightGrey};

  @media only screen and (max-width: ${breakpoints.extraLargeDesktop}) {
    border: none;
    border-bottom: 1px solid ${colors.mediumLightGrey};
    box-shadow: none;
  }
`

const ApplicantWrapper = styled.div`
  display: flex;
  align-items: center;
  max-width: 200px;

  @media only screen and (max-width: ${breakpoints.desktop}) {
    max-width: 180px;
  }

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    max-width: 190px;
    margin: 0;
  }
`

const HeartImage = styled.img`
  height: 24px;
  margin: 0 5px;
  cursor: pointer;

  :hover,
  :active {
    opacity: 0.7;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin: 8px 0;

    :hover {
      opacity: 1;
    }
  }
`

const formatDate = date => moment(date, 'YYYY-MM-DD').format('MMM DD, YYYY')

const getApplicationFavouriteHeader = currentFavourite =>
  currentFavourite ? HeartSelected : HeartUnselected

const getApplicationFavourite = app => (isTruthy(app.favourite) ? HeartSelected : HeartUnselected)

const OptionsMenu = ({ application, listingId, history }) => {
  const { t } = useTranslation()
  const myProfile = useProfile()
  const allowApplicationCopy = myProfile?.company_info?.registration_plan === 'enterprise'
  return (
    <Box justifyContent="center">
      <OverflowMenu label="Options" id="options">
        <MenuItemLink href={`/listing/${listingId}/contract/choose/types`}>
          {t('l.application.table.options.create_contract', 'Create Contract')}
        </MenuItemLink>
        {allowApplicationCopy && (
          <MenuItem
            onClick={() => {
              showCopyApplicationModal({ application, history, listingId })
            }}
          >
            {t('l.application.table.options.copy_application', 'Copy to Another Listing')}
          </MenuItem>
        )}

        <MenuItemLink
          href={`${config.BACKEND_SERVER}/applications/${application?.id}/application.pdf`}
          target="_blank"
        >
          {t('l.application.table.options.export_pdf', 'Export to PDF')}
        </MenuItemLink>
      </OverflowMenu>
    </Box>
  )
}

const LoadingState = () => {
  return (
    <Box display="flex" justifyContent="center" padding="xlarge">
      <LoadingCircle />
    </Box>
  )
}

const RenderApplicant = ({ application }) => {
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  return (
    <ApplicantWrapper>
      {!isMobile && (
        <Avatar
          left="0"
          right={10}
          height={32}
          width={32}
          shadow
          location={
            application.avatar?.aws_s3_key
              ? getImageUrl(application.avatar.aws_s3_key, {
                  width: 100,
                  height: 100
                })
              : undefined
          }
        />
      )}

      <Text size="standard" weight="medium">
        {formatName(application)}
      </Text>
    </ApplicantWrapper>
  )
}

const RenderFavourite = ({ application }) => {
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const handleFavouriteApplication = app => {
    isTruthy(app.favourite)
      ? dispatch.application.unfavourite(app.id)
      : dispatch.application.favourite(app.id)
  }

  return (
    <StyledTableCell
      colSpan="1"
      width="10%"
      paddingTop={isMobile ? '6px' : '0px'}
      verticalAlign={isMobile ? 'top' : 'middle'}
      onClick={e => {
        e.stopPropagation()
        handleFavouriteApplication(application)
      }}
    >
      {!isTruthy(application.favourite) ? (
        <TooltipRenderer
          tooltip={
            <Box paddingY="xxxsmall" justifyContent="center">
              <Text size="small">
                {t('l.application.table.heart_unselected.add_to_favourites', 'Add to favourites')}
              </Text>
            </Box>
          }
        >
          {({ triggerProps }) => (
            <Box
              {...triggerProps}
              display="flex"
              alignItems="center"
              justifyContent="center"
              paddingX={{ mobile: 'gutter', tablet: 'small' }}
            >
              <HeartImage src={getApplicationFavourite(application)} />
            </Box>
          )}
        </TooltipRenderer>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          paddingX={{ mobile: 'gutter', tablet: 'small' }}
        >
          <HeartImage src={getApplicationFavourite(application)} />
        </Box>
      )}
    </StyledTableCell>
  )
}

const RenderMobileApplicationRow = ({ application, listingId, history }) => {
  const { t } = useTranslation()

  return (
    <>
      <RenderFavourite application={application} />
      <StyledTableCell colSpan="7" width="75%">
        <MobileRowWrapper marginLeft={0}>
          <MobileUnitText>
            <RenderApplicant application={application} />
          </MobileUnitText>
          <MobileUnitDetails top={20}>
            <p>
              <Stack space="xsmall">
                <Text size="small">
                  {t('l.listing.application.mobile.salary.text', 'Annual Income:')}{' '}
                  {formatSalaryRange(application.salary_range, t)}
                </Text>
                <Box display="flex" alignItems="center" gap="xsmall">
                  <Text size="small">
                    {t('l.listing.application.liv_score.text', 'Trust Score')}:{' '}
                  </Text>
                  <TrustScoreComponent application={application} />
                </Box>
              </Stack>
            </p>
          </MobileUnitDetails>
        </MobileRowWrapper>
      </StyledTableCell>
      <StyledTableCell colSpan="1" width="15%">
        <MobileUnitText justifyContent>
          <OptionsMenu application={application} listingId={listingId} history={history} />
        </MobileUnitText>
      </StyledTableCell>
    </>
  )
}

const RenderApplicationRow = ({ application, listingId, history }) => {
  const { t } = useTranslation()
  const displayDesktop = useMediaQuery(`(max-width: ${breakpoints.desktop})`)
  return (
    <>
      <RenderFavourite application={application} />
      <StyledTableCell justifyContent="flex-start">
        <Box padding="xsmall">
          <RenderApplicant application={application} />
        </Box>
      </StyledTableCell>
      <StyledTableCell justifyContent="flex-start">
        <Box padding="xsmall">
          <Text size="standard">{formatSalaryRange(application.salary_range, t)}</Text>
        </Box>
      </StyledTableCell>
      <StyledTableCell justifyContent="flex-start">
        <Box padding="xsmall">
          <TrustScoreComponent application={application} />
        </Box>
      </StyledTableCell>
      <StyledTableCell justifyContent="flex-start">
        <Box padding="xsmall">
          <Text>{formatDate(application.created_at)}</Text>
        </Box>
      </StyledTableCell>
      <StyledTableCell>
        <Box padding="xsmall">
          <ButtonLink
            target="_blank"
            href={`/tenant/${listingId}/chat/${application?.timeline_room}?listing_id=${listingId}`}
            onClick={e => {
              e.stopPropagation()
            }}
            variant="solid"
            state="default"
            size="small"
            label="Chat"
            iconPosition="leading"
            id={`applications-table-chat-click-${listingId}`}
          >
            {t('common.chat', 'Chat')}
          </ButtonLink>
        </Box>
      </StyledTableCell>
      {!displayDesktop && (
        <StyledTableCell>
          <Box padding="xsmall">
            <ButtonLink
              href={`/listings/${listingId}/applications/${application.id}/trust-score`}
              onClick={e => {
                e.stopPropagation()
              }}
              variant="ghost"
              state="default"
              tone="default"
              size="small"
              label="View"
              id={`applications-table-view-click-${listingId}`}
            >
              {t('l.listing.list.view.text', 'View')}
            </ButtonLink>
          </Box>
        </StyledTableCell>
      )}

      <StyledTableCell>
        <Box padding="xsmall" marginLeft="xxxsmall">
          <OptionsMenu application={application} listingId={listingId} history={history} />
        </Box>
      </StyledTableCell>
    </>
  )
}

const ListingApplications = ({
  history,
  match: {
    params: { listingId }
  }
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const displayDesktop = useMediaQuery(`(max-width: ${breakpoints.desktop})`)
  const displayDesktopSmall = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)
  const [applications, setApplications] = useState([])
  const [currentLoadedLength, setCurrentLoadedLength] = useState([])
  const [rowsLoading, setRowsLoading] = useState(false)
  const [isDataChanging, setIsDataChanging] = useState(false)
  const [applicationsLoaded, setApplicationsLoaded] = useState(false)
  const [currentSort, setCurrentSort] = useState({
    prop: 'none',
    direction: 'none'
  })
  const [currentFavourite, setCurrentFavourite] = useState(false)
  const [currentSearchString, setCurrentSearchString] = useState('')

  const loadData = () => {
    setCurrentLoadedLength(currentLoadedLength + 20)
  }

  useEffect(() => {
    if (!rowsLoading) return
    loadData()
  }, [rowsLoading])

  const onScroll = useMemo(
    () =>
      throttle(async event => {
        const el = event
        const scrollTop = el.target.children[0].scrollTop
        const scrollHeight = el.target.children[0].scrollHeight - el.target.children[0].clientHeight
        const ratio = scrollTop / scrollHeight
        if (ratio > 0.9 && currentLoadedLength < applications.length) {
          setRowsLoading(true)
        }
      }, 300),
    [currentLoadedLength, applications.length]
  )
  useEventListener('scroll', onScroll)

  const updateCurrentSort = propName => {
    if (currentSort.direction === 'asc' && currentSort.prop === propName) {
      setCurrentSort({
        prop: propName,
        direction: 'desc'
      })
    } else if (currentSort.direction === 'desc' && currentSort.prop === propName) {
      setCurrentSort({
        prop: 'none',
        direction: 'none'
      })
    } else {
      setCurrentSort({
        prop: propName,
        direction: 'asc'
      })
    }
  }

  useEffect(() => {
    if (applicationsLoaded) return
    const load = async () => {
      await dispatch.listing.getApplications(listingId)
      setApplicationsLoaded(true)
    }
    load()
  }, [dispatch.listing, listingId, applicationsLoaded])

  const apps = useSelector(select.listing.applications)

  useEffect(() => {
    const initialSortedData = apps.sort((a, b) => {
      if (a.first_name > b.first_name) {
        return 1
      } else if (a.first_name < b.first_name) {
        return -1
      }
      return 0
    })
    setApplications(initialSortedData)
    if (apps.length > 0) setCurrentLoadedLength(20)
    else setCurrentLoadedLength(0)
  }, [apps])

  // Handle search
  const searchApplications = searchString => {
    setCurrentSearchString(searchString)
    setIsDataChanging(true)
    const filtered = apps.filter(app => {
      const searchFields = values(pipe(pick(['first_name', 'preferred_name', 'last_name']))(app))
      return JSON.stringify(searchFields).toLowerCase().includes(searchString.trim().toLowerCase())
    })
    setApplications(filtered)
  }

  const getSortedData = () => {
    let sortedData = applications
    if (currentSort.prop === 'none') {
      sortedData = applications.sort((a, b) => {
        if (a.favourite === b.favourite) {
          if (a.first_name > b.first_name) {
            return currentSort.direction === 'asc' || currentSort.prop === 'none' ? 1 : -1
          } else if (a.first_name < b.first_name) {
            return currentSort.direction === 'asc' || currentSort.prop === 'none' ? -1 : 1
          }
          return 0
        } else if (a.favourite) return -1
        else return 1
      })
    } else if (currentSort.prop === 'name') {
      sortedData = applications.sort((a, b) => {
        if (a.first_name > b.first_name) {
          return currentSort.direction === 'asc' || currentSort.prop === 'none' ? 1 : -1
        } else if (a.first_name < b.first_name) {
          return currentSort.direction === 'asc' || currentSort.prop === 'none' ? -1 : 1
        }
        return 0
      })
    } else if (currentSort.prop === 'salary_sort') {
      sortedData = applications.sort((a, b) => {
        return currentSort.direction === 'asc'
          ? Number(b.salary_sort) - Number(a.salary_sort)
          : Number(a.salary_sort) - Number(b.salary_sort)
      })
    } else if (currentSort.prop === 'created_at') {
      sortedData = applications.sort((a, b) => {
        return currentSort.direction === 'asc'
          ? new Date(b.created_at.split(' ')[0]) - new Date(a.created_at.split(' ')[0])
          : new Date(a.created_at.split(' ')[0]) - new Date(b.created_at.split(' ')[0])
      })
    } else if (currentSort.prop === 'overall') {
      sortedData = applications.sort((a, b) => {
        let a_score =
          a.trust_score !== undefined
            ? a.trust_score.score
            : a.overall !== undefined
              ? a.overall
              : 0
        let b_score =
          b.trust_score !== undefined
            ? b.trust_score.score
            : b.overall !== undefined
              ? b.overall
              : 0
        return currentSort.direction === 'asc'
          ? Number(b_score) - Number(a_score)
          : Number(a_score) - Number(b_score)
      })
    }
    return sortedData
  }

  useEffect(() => {
    setIsDataChanging(false)
    setRowsLoading(false)
  }, [applications, currentLoadedLength])

  const overallSortedData = useMemo(
    () => getSortedData(),
    [currentSort.direction, currentSort.prop, applications]
  )

  const breadcrumbs = [
    {
      to: '/listings',
      label: t('l.listing.view.all_listings.link', 'All Listings')
    },
    {
      to: `/listing/${listingId}`,
      label: t('l.listing.view.dashboard.link', 'Dashboard')
    },
    {
      to: null,
      label: t('l.listing.view.listing_dashboard_applications.title', 'Applications')
    }
  ]

  const handleApplicationClick = application => {
    pushToDatalayer({
      event: 'listing-dashboard-application-click'
    })
    history.push(`/listings/${listingId}/applications/${application.id}/trust-score`, {
      prev: history.location.pathname
    })
  }

  return (
    <Wrapper>
      <Helmet>
        <title>{t('l.listing.view.listing_dashboard_applications.title', 'Applications')}</title>
      </Helmet>
      <ListingApplicationsHeader
        breadcrumbs={breadcrumbs}
        currentSort={currentSort}
        setCurrentSort={setCurrentSort}
        listingId={listingId}
        onSearch={searchApplications}
      />

      <TableOutterWrapper>
        <TableWrapper>
          <ApplicationTable>
            {!isMobile && (
              <TableHeader>
                <TableRow
                  sticky
                  top={
                    !displayDesktop
                      ? structure.header.heightMobile + 136
                      : !displayDesktopSmall
                        ? structure.header.heightMobile + 132
                        : !isMobile
                          ? structure.header.heightMobile + 107
                          : structure.header.heightMobile + 70
                  }
                >
                  <TableHeaderCell width="7%">
                    <TableHeaderText margin="0">
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        paddingX="small"
                      >
                        <HeartImage src={getApplicationFavouriteHeader(currentFavourite)} />
                      </Box>
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('name')
                    }}
                    active={currentSort.prop === 'name'}
                    ascending={currentSort.direction === 'asc'}
                    alpha
                    width="25%"
                  >
                    <TableHeaderText alignLeft>
                      {t('l.listing.application.applicant.text', 'Applicant')}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('salary_sort')
                    }}
                    active={currentSort.prop === 'salary_sort'}
                    ascending={currentSort.direction === 'asc'}
                    width="25%"
                  >
                    <TableHeaderText margin={'0 15px 0 0'} alignLeft>
                      {t('l.listing.application.income.text', 'Approx. Annual Income')}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('overall')
                    }}
                    active={currentSort.prop === 'overall'}
                    ascending={currentSort.direction === 'asc'}
                    width="25%"
                  >
                    <TableHeaderText margin={'0 15px 0 0'} alignLeft>
                      {t('l.listing.application.liv_score.text', 'Trust Score')}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('created_at')
                    }}
                    active={currentSort.prop === 'created_at'}
                    ascending={currentSort.direction === 'asc'}
                    width="25%"
                  >
                    <TableHeaderText margin={'0 15px 0 0'} alignLeft>
                      {t('l.listing.application.date_applied.text', 'Date Applied')}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell width="89px">
                    <TableHeaderText>&nbsp;</TableHeaderText>
                  </TableHeaderCell>
                  {!displayDesktop && (
                    <TableHeaderCell width="90px">
                      <TableHeaderText>&nbsp;</TableHeaderText>
                    </TableHeaderCell>
                  )}

                  <TableHeaderCell width="56px">
                    <TableHeaderText>&nbsp;</TableHeaderText>
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
            )}
            <TableBody>
              {applicationsLoaded && !isDataChanging ? (
                <>
                  {overallSortedData.length > 0 ? (
                    overallSortedData.slice(0, currentLoadedLength).map(application => (
                      <SingleTableRow
                        key={application.id}
                        onClick={() => handleApplicationClick(application)}
                        data-testid={`application-${application.id}`}
                      >
                        <ResponsiveRender below="tablet">
                          <RenderMobileApplicationRow
                            application={application}
                            listingId={listingId}
                            history={history}
                          />
                        </ResponsiveRender>
                        <ResponsiveRender above="mobile">
                          <RenderApplicationRow
                            application={application}
                            listingId={listingId}
                            history={history}
                          />
                        </ResponsiveRender>
                      </SingleTableRow>
                    ))
                  ) : currentSearchString.length > 0 ? (
                    <SingleTableRow nonClickable>
                      <StyledTableCell colSpan={8}>
                        <ApplicationsEmptyListComponent />
                      </StyledTableCell>
                    </SingleTableRow>
                  ) : (
                    <SingleTableRow nonClickable>
                      <StyledTableCell colSpan={8}>
                        <EmptyApplications listingId={listingId} />
                      </StyledTableCell>
                    </SingleTableRow>
                  )}
                </>
              ) : (
                <SkeletonState />
              )}
              <ApplicationHighlight
                onClick={() => {
                  history.push('/')
                }}
              />
              <ContractHighlight
                link={
                  applications[0]
                    ? `/listings/${listingId}/applications/${applications[0].id}`
                    : '/'
                }
              />
            </TableBody>
          </ApplicationTable>
        </TableWrapper>
      </TableOutterWrapper>
      {rowsLoading && <LoadingState />}
    </Wrapper>
  )
}

export default memo(ListingApplications)
